/**
 * Creates a pricing API requester.
 *
 * Always fires two parallel requests — one for annual (yearly) and one for
 * monthly pricing — so that both datasets are available for savings messages
 * regardless of the currently selected term.
 *
 * @param {Object}   options           Configuration object.
 * @param {string}   options.apiUrl    Base pricing API URL.
 * @param {Function} options.getParams Returns current API request parameters (term, coreSeats, ultimateSeats).
 * @param {Function} options.onStart   Callback fired before the requests start.
 * @param {Function} options.onSuccess Callback fired with { annualData, monthlyData }.
 * @param {Function} options.onError   Callback fired when any request fails.
 * @return {Function} Pricing request function.
 */
export const createPricingRequester = ( {
	apiUrl,
	getParams,
	onStart,
	onSuccess,
	onError,
} ) => {

	let requestController = null;
	const DEBUG_STORAGE_KEY = 'pricingCalculatorDebug';

	/**
	 * Returns true when pricing debug mode is enabled.
	 *
	 * Debug can be enabled by:
	 * - URL query param: ?pcDebug=1
	 * - localStorage key: pricingCalculatorDebug=1
	 *
	 * @return {boolean} Debug flag.
	 */
	const isDebugEnabled = () => {

		const queryValue = new URLSearchParams( window.location.search ).get( 'pcDebug' );
		const storageValue = window.localStorage.getItem( DEBUG_STORAGE_KEY );

		return queryValue === '1' || storageValue === '1';

	};

	/**
	 * Logs debug output for pricing API flow.
	 *
	 * @param {...unknown} args Console log arguments.
	 * @return {void}
	 */
	const logDebug = ( ...args ) => {

		console.log( '[Pricing Calculator]', ...args );

	};

	/**
	 * Builds a pricing API URL for a specific term.
	 *
	 * @param {Object} params               Request parameters.
	 * @param {string} params.term          Billing period ('annual' or 'monthly').
	 * @param {number} params.coreSeats     Core seat count.
	 * @param {number} params.ultimateSeats Ultimate seat count.
	 * @param {number} params.extendedSeats Extended seat count.
	 * @return {string} Pricing API URL.
	 */
	const buildRequestUrl = ( {
		term,
		coreSeats,
		ultimateSeats,
		extendedSeats,
	} ) => {

		const url = new URL( apiUrl, window.location.href );

		url.searchParams.set( 'term', term === 'annual' ? 'yearly' : term );
		url.searchParams.set( 'core_seats', String( coreSeats ) );
		url.searchParams.set( 'ultimate_seats', String( ultimateSeats ) );
		url.searchParams.set( 'extended_seats', String( extendedSeats ) );

		return url.toString();

	};

	/**
	 * Registers a global debug helper for manual API inspection from browser console.
	 *
	 * Usage examples:
	 * - window.pricingCalculatorDebug.enable()
	 * - window.pricingCalculatorDebug.fetchRaw({ term: 'yearly', coreSeats: 25, ultimateSeats: 0 })
	 * - window.pricingCalculatorDebug.fetchRaw({ term: 'monthly', coreSeats: 0, ultimateSeats: 30, extraParams: { coupon: 'test' } })
	 *
	 * @return {void}
	 */
	const registerDebugHelper = () => {

		if ( window.pricingCalculatorDebug?.fetchRaw ) {

			return;

		}

		window.pricingCalculatorDebug = {
			/**
			 *
			 */
			enable: () => {

				window.localStorage.setItem( DEBUG_STORAGE_KEY, '1' );
				console.log( '[Pricing Calculator] Debug enabled' );

			},
			/**
			 *
			 */
			disable: () => {

				window.localStorage.removeItem( DEBUG_STORAGE_KEY );
				console.log( '[Pricing Calculator] Debug disabled' );

			},
			/**
			 * Returns current debug mode state.
			 *
			 * @return {boolean} Whether debug mode is enabled.
			 */
			isEnabled: () => isDebugEnabled(),
			/**
			 * Sends one raw request to the pricing API and prints the full JSON.
			 *
			 * @param {Object} options Request options.
			 * @param {string} [options.term='yearly'] Term expected by API ('yearly' | 'monthly').
			 * @param {number} [options.coreSeats=0] Core seats.
			 * @param {number} [options.ultimateSeats=0] Ultimate seats.
			 * @param {number} [options.extendedSeats=0] Extended seats.
			 * @param {Object} [options.extraParams={}] Extra query params to append.
			 * @param {string} [options.apiUrlOverride] Optional endpoint override.
			 * @return {Promise<Object>} Raw JSON API response.
			 */
			fetchRaw: async ( {
				term = 'yearly',
				coreSeats = 0,
				ultimateSeats = 0,
				extendedSeats = 0,
				extraParams = {},
				apiUrlOverride,
			} = {} ) => {

				const requestUrl = new URL( apiUrlOverride || apiUrl, window.location.href );

				requestUrl.searchParams.set( 'term', term );
				requestUrl.searchParams.set( 'core_seats', String( coreSeats ) );
				requestUrl.searchParams.set( 'ultimate_seats', String( ultimateSeats ) );
				requestUrl.searchParams.set( 'extended_seats', String( extendedSeats ) );

				Object.entries( extraParams ).forEach( ( [ key, value ] ) => {

					if ( value === undefined || value === null ) {

						return;

					}

					requestUrl.searchParams.set( key, String( value ) );

				} );

				console.log( '[Pricing Calculator] Debug raw request URL:', requestUrl.toString() );

				const response = await fetch( requestUrl.toString() );

				if ( ! response.ok ) {

					throw new Error( `API error ${ response.status }` );

				}

				const data = await response.json();

				console.log( '[Pricing Calculator] Debug raw response:', data );

				return data;

			},
		};

	};

	registerDebugHelper();

	/**
	 * Requests pricing data for both annual and monthly terms in parallel.
	 *
	 * @return {void}
	 */
	return () => {

		const {
			term,
			coreSeats,
			ultimateSeats,
			extendedSeats,
		} = getParams();

		const annualUrl  = buildRequestUrl( { term: 'annual',  coreSeats, ultimateSeats, extendedSeats } );
		const monthlyUrl = buildRequestUrl( { term: 'monthly', coreSeats, ultimateSeats, extendedSeats } );

		logDebug( 'Request payload:', {
			term,
			coreSeats,
			ultimateSeats,
			extendedSeats,
		} );
		logDebug( 'Request URLs:', {
			annualUrl,
			monthlyUrl,
		} );

		if ( requestController ) {

			requestController.abort();

		}

		const controller = new AbortController();
		requestController = controller;

		onStart();

		Promise.all( [
			fetch( annualUrl,  { signal: controller.signal } ),
			fetch( monthlyUrl, { signal: controller.signal } ),
		] )
			.then( ( [ annualResponse, monthlyResponse ] ) => {

				if ( ! annualResponse.ok ) {

					throw new Error( `API error ${ annualResponse.status }` );

				}

				if ( ! monthlyResponse.ok ) {

					throw new Error( `API error ${ monthlyResponse.status }` );

				}

				return Promise.all( [ annualResponse.json(), monthlyResponse.json() ] );

			} )
			.then( ( [ annualData, monthlyData ] ) => {

				if ( requestController !== controller ) {

					return;

				}

				requestController = null;
				logDebug( 'API responses:', {
					annualData,
					monthlyData,
				} );
				onSuccess( { annualData, monthlyData } );

			} )
			.catch( ( error ) => {

				if ( error.name === 'AbortError' ) {

					return;

				}

				if ( requestController === controller ) {

					requestController = null;

				}

				logDebug( 'API request failed:', error );
				onError( error );

			} );

	};

};
